import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {API_URL} from '../constants';

// -------------------- Event Models --------------------
export interface EventDTO {
    id?: string;
    title: string;
    chapterId: string;
    msgId: string;
    createdBy: string;
    createdByName?: string; // Not from API, used for display
    createdDate?: string;
    updatedBy?: string;
    updatedDate?: string;
    status?: string;
    eventStartDate: string | Date;
    eventStartTime: string;
    eventEndDate: string;
    eventEndTime: string;
    eventLocation: string;
    eventDescription: string;
    eventType: string; // free or paid
    eventScope?: 'NEC' | 'CHAPTER';
    eventFeeAdult?: string;
    eventFeeChild?: string;
    eventFeeSeniorCitizen?: string;
    childAgeMin?: string;
    childAgeLimit?: string;
    fromEmail?: string;
    additionalEmailIds?: string[];
    ccEmailIds?: string[]; // CC'd on registration/payment confirmation emails for this event
    eventRegistrations?: EventRegistrationDTO[];
    expenses?: EventExpenseDTO[];
    photos?: EventPhotoDTO[];
    finalBalance?: number;
    eventCategory?: 'LOCAL' | 'CONFERENCE'; // null/LOCAL = legacy flow; CONFERENCE = accommodation booking
    ticketOptions?: EventTicketOption[];
    registrationQuestions?: EventFormQuestion[]; // organizer-defined radio/checkbox questions; undefined/null = legacy event (hardcoded attending radio)
    notifyLandingPage?: boolean; // NEC events only: while true and status=OPEN, shows a promo notification on the public landing page
    zellePaymentDetails?: string; // email/phone shown to registrants who choose "Pay with Zelle"
    checkPaymentDetails?: string; // payable-to/mailing info shown to registrants who choose "Pay by Check"
    stripePaymentEnabled?: boolean; // undefined (pre-existing events) or true = "Pay with Card" offered; false hides it
    hasCustomStripeKeys?: boolean; // derived, write-only indicator — true when this event has its own Stripe account
    eventCommittee?: EventCommitteeMember[]; // optional — shown on the event page only when non-empty
    eventGuestsOfHonor?: EventCommitteeMember[]; // optional — same shape as eventCommittee, shown as its own "Guest of Honors" section when non-empty
    eventSpeakers?: EventSpeaker[]; // optional — shown on the event page only when non-empty

    // Early bird / date-window discount, applied to registration fees only (never the donation
    // amount). Active only while today's date falls within [discountStartDate, discountEndDate]
    // (YYYY-MM-DD, inclusive) — see calculateEventDiscount()/isDiscountActive() below.
    discountLabel?: string; // e.g. "Early Bird Discount" — shown next to the discounted total
    discountType?: 'FLAT' | 'PERCENT';
    discountValue?: string;
    discountStartDate?: string; // YYYY-MM-DD
    discountEndDate?: string; // YYYY-MM-DD
}

// -------------------- Payment helpers --------------------
// Events saved before the flag existed have no value — treat that as enabled so they keep offering card payment.
export function isStripePaymentEnabled(event: EventDTO | null | undefined): boolean {
    return event?.stripePaymentEnabled !== false;
}

// -------------------- Discount helpers --------------------
// True while today's date falls within the event's configured discount window (inclusive).
export function isDiscountActive(event: EventDTO | null | undefined): boolean {
    if (!event || !event.discountStartDate || !event.discountEndDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(event.discountStartDate + 'T00:00:00');
    const end = new Date(event.discountEndDate + 'T23:59:59');
    return today >= start && today <= end;
}

// Amount (in dollars) to subtract from a registration-fees subtotal, based on the event's
// configured discount. Applies only to fees — callers must exclude any donation amount from
// feesSubtotal before calling this. Returns 0 when no discount is configured, the discount
// window isn't active, or the configured value is invalid.
export function calculateEventDiscount(event: EventDTO | null | undefined, feesSubtotal: number): number {
    if (!event || !event.discountValue || !event.discountType) return 0;
    if (feesSubtotal <= 0) return 0;
    if (!isDiscountActive(event)) return 0;

    const value = parseFloat(event.discountValue);
    if (isNaN(value) || value <= 0) return 0;

    const discount = event.discountType === 'PERCENT'
        ? feesSubtotal * (value / 100)
        : value;

    // Never discount below zero, and never discount more than the fees themselves.
    return Math.min(Math.max(discount, 0), feesSubtotal);
}

// Minimal, public-safe projection of an event for anonymous nav-menu listings.
export interface PublicEventSummaryDTO {
    id: string;
    title: string;
    eventStartDate?: string;
    eventEndDate?: string;
    eventLocation?: string;
}

export interface EventFormOption {
    optionId?: string;
    label: string;
}

export interface EventFormQuestion {
    questionId?: string;
    label: string;
    type: 'SINGLE_SELECT' | 'MULTI_SELECT'; // radio vs checkboxes
    required: boolean;
    options: EventFormOption[];
}

// An attendee's answer. Labels are snapshotted by the server so later question edits never change past answers.
export interface EventFormAnswer {
    questionId: string;
    questionLabel?: string;
    selectedOptionIds: string[];
    selectedLabels?: string[];
}

export interface EventRegistrationDTO {
    userId: string;
    adults?: number;
    children?: number;
    seniors?: number;
    billableAdults?: number;
    billableChildren?: number;
    childAges?: string;
    status?: string;
    donationAmt?: number;
    paymentType?: 'stripe' | 'zelle' | 'check' | 'later';
    paymentReference?: string;
    registrationSource?: string; // PUBLIC or PORTAL
    registrationDate?: string;
    formAnswers?: EventFormAnswer[];
    refundStatus?: 'PARTIALLY_REFUNDED' | 'REFUNDED'; // set when NEC refunded the card payment; NEC reviews it
    // Read-only money view (from the registration's booking).
    bookingId?: string;
    amountDue?: number;
    amountPaid?: number;
    balance?: number;
    paymentState?: PaymentState;
}

export type PaymentState = 'PENDING' | 'PAID' | 'BALANCE_DUE' | 'OVERPAID';

export interface EventBookingPaymentDTO {
    paymentId?: string;
    kind: 'PAYMENT' | 'REFUND';
    method?: string;
    amount: number;
    reference?: string;
    recordedBy?: string;
    recordedAt?: string;
}

/** What the registrant or an organizer may change on an existing registration. Omitted = unchanged. */
export interface RegistrationEditRequest {
    selectedOptions?: EventBookingOption[];
    numAdults?: number;
    numChildren?: number;
    numSeniors?: number;
    childAges?: string;
    donationAmt?: number;
    attending?: 'YES' | 'NO';
    formAnswers?: EventFormAnswer[];
    paymentType?: string;
    paymentReference?: string;
    reason?: string;
}

/** Records money received by hand and (re)confirms. No amount = the full balance. */
export interface RegistrationConfirmRequest {
    amount?: number;
    method?: string;
    reference?: string;
    waiveBalance?: boolean;
    note?: string;
    /** true = money sent back to the registrant (check, Zelle, cash…) rather than received. */
    refund?: boolean;
}

/** Sends a confirmed registration back to Payment Pending so it can be changed. */
export interface RegistrationReopenRequest {
    reason: string;
}

export interface RegistrationChangeDTO {
    id: string;
    action: 'EDIT' | 'CONFIRM' | 'WAIVE' | 'REFUND' | 'REOPEN' | 'NO_SHOW' | 'RESTORE';
    changedBy?: string;
    changedByKind?: 'MEMBER' | 'COMMITTEE' | 'CHAPTER_EXEC' | 'NEC';
    changedAt?: string;
    reason?: string;
    changes?: { field: string; from?: string; to?: string }[];
    amountDueBefore?: number;
    amountDueAfter?: number;
    amountPaidAfter?: number;
}

export interface EventExpenseDTO {
    expenseId?: string;
    eventId?: string;
    category: string;
    amount: number;
    addedBy?: string;
    spentBy?: string;   // userId of the member who actually spent the money
    addedDate?: string;
    receiptFileName?: string;
    receiptContentType?: string;
    receiptUploadedDate?: string;
}

export interface ExpenseReceiptUploadResponse {
    eventId?: string;
    filename: string;
    contentType: string;
    uploadedDate: string;
    uploadUrl?: string;
    status?: string;
}

export interface EventPhotoDTO {
    photoId?: string;
    url: string;
    description?: string;
    uploadedBy?: string;
    uploadedDate?: string;
    moderated?: boolean;
}

export interface EventCommentDTO {
    commentId?: string;
    photoId?: string;
    userId: string;
    comment: string;
    createdDate?: string;
}

export interface EventEmailMessageDTO {
    messageId?: string;
    subject: string;
    body: string;
    fromEmail?: string;
    additionalEmailIds?: string[];
    createdBy?: string;
    createdDate?: string;
    updatedBy?: string;
    updatedDate?: string;
}

export interface EventTicketOption {
    optionId?: string;
    label: string;          // user-configurable display name — always render from data, never hardcode
    subText?: string;       // optional smaller text rendered under the label
    price: string;
    sectionLabel?: string;  // groups this option under a named panel/section
    paid?: boolean;         // false = informational only, no price charged/shown; defaults to true
    required?: boolean;     // true = registrant must enter a quantity > 0 for this option before submitting
    groupRequired?: boolean; // true = at least one option sharing this sectionLabel must have quantity > 0 (set on every option in the group)
}

export interface EventCommitteeMember {
    memberId?: string;
    name: string;
    title?: string;          // role/title, e.g. "Chapter President", "Program Chair"
    shortDescription?: string;
    photoUrl?: string;       // optional — falls back to an initials avatar when blank
    videoUrl?: string;       // optional — rendered as a "Watch More" link
}

export interface EventSpeaker {
    speakerId?: string;
    name: string;
    role?: string;            // e.g. "Keynote Speaker", "Featured Speaker"
    company?: string;
    topic?: string;
    topicDescription?: string; // optional short description of the talk/topic, shown under the topic title
    tags?: string[];
    photoUrl?: string;       // optional — falls back to an initials avatar when blank
    videoUrl?: string;       // optional — rendered as a "Watch More" link
}

export type EventParticipantType = 'COMMITTEE' | 'GUEST_OF_HONOR' | 'SPEAKER';

// Row in the EventParticipant table — replaces the old embedded eventCommittee/eventGuestsOfHonor/
// eventSpeakers arrays on EventDTO. userId is set only for COMMITTEE entries: it links the entry to a
// real member account, which is what grants that member elevated permissions on this one event.
export interface EventParticipantDTO {
    id?: string;
    eventId?: string;
    participantType: EventParticipantType;
    userId?: string; // COMMITTEE only
    name: string;
    title?: string;          // committee/guest-of-honor role/title, or the speaker's role
    shortDescription?: string; // committee/guest-of-honor bio
    company?: string;         // speaker only
    topic?: string;           // speaker only
    topicDescription?: string; // speaker only
    tags?: string[];          // speaker only
    photoUrl?: string;
    videoUrl?: string;
    createdBy?: string;
    createdDate?: string;
    updatedBy?: string;
    updatedDate?: string;
}

export interface CardPaymentDTO {
    id: string;
    userId?: string;
    bookingId?: string;
    paymentStatus?: string; // CREATED | PROCESSING | PAID | PARTIALLY_REFUNDED | REFUNDED | DISPUTED | EXPIRED | FAILED | CANCELLED
    amount?: number;
    stripeFee?: number;
    amountRefunded?: number;
    createdEpoch?: number;
    lastSyncedAt?: string;
    failureReason?: string;
    payoutStatus?: string;
}

export interface EventBookingOption {
    optionId: string;
    quantity: number;
}

export interface EventBookingDTO {
    id?: string;
    eventId: string;
    userId: string;
    selectedOptions?: EventBookingOption[];
    ticketOptionId?: string;
    numAdults?: number;
    numChildren?: number;
    numAddOns?: number;
    numSeniors?: number;
    childAges?: string;
    status?: string;
    paymentType?: 'stripe' | 'zelle' | 'check' | 'later';
    paymentReference?: string;
    donationAmt?: number;
    totalAmount?: number;
    registrationSource?: string;
    attending?: 'YES' | 'NO'; // legacy: only set for events without registrationQuestions
    formAnswers?: EventFormAnswer[];
    refundStatus?: 'PARTIALLY_REFUNDED' | 'REFUNDED'; // set when NEC refunded the card payment; NEC reviews it
    amountDue?: number;
    amountPaid?: number;
    balance?: number;
    paymentState?: PaymentState;
    payments?: EventBookingPaymentDTO[];
    createdBy?: string;
    createdDate?: string;
    updatedBy?: string;
    updatedDate?: string;
}

export interface AnonymousBookingVerifyResponse {
    valid: boolean;
    userId: string;
    firstName: string;
    lastName: string;
    spouseName?: string;
    event?: EventDTO;
    existingBooking?: EventBookingDTO;
}

export interface AnonymousEventVerifyResponse {
    valid: boolean;
    userId: string;
    firstName: string;
    lastName: string;
    spouseName?: string;
    event?: EventDTO;
    existingRegistration?: EventRegistrationDTO;
}

export interface QuickSignupRequest {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    state: string;
    city: string;
}

export interface QuickSignupResponse {
    userId: string;
    firstName: string;
    lastName: string;
}

export interface EventPhotoWithComments {
    photoId?: string;
    url: string;
    description?: string;
    uploadedBy?: string;
    uploadedDate?: string;
    moderated?: boolean;
    comments?: EventCommentDTO[];
}

// -------------------- Service --------------------
@Injectable({
    providedIn: 'root',
})
export class EventService {
    private apiUrl = `${API_URL}events`;

    constructor(private http: HttpClient) {
    }

    // -------------------- Events --------------------
    getAllEvents(): Observable<EventDTO[]> {
        return this.http.get<EventDTO[]>(this.apiUrl);
    }

    getEventById(id: string): Observable<EventDTO> {
        return this.http.get<EventDTO>(`${this.apiUrl}/${id}`);
    }

    createEvent(event: EventDTO): Observable<EventDTO> {
        return this.http.post<EventDTO>(this.apiUrl, event);
    }

    updateEvent(id: string, event: EventDTO): Observable<EventDTO> {
        return this.http.put<EventDTO>(`${this.apiUrl}/${id}`, event);
    }

    // Pass both keys to set a per-event Stripe account, or neither to clear the override
    // and fall back to the chapter's default keys.
    updateEventStripeKeys(id: string, stripePublicKey: string, stripeSecretKey: string): Observable<EventDTO> {
        return this.http.put<EventDTO>(`${this.apiUrl}/${id}/stripe-keys`, {stripePublicKey, stripeSecretKey});
    }

    deleteEvent(id: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${id}`);
    }

    getEventsByChapterId(chapterId: string): Observable<EventDTO[]> {
        return this.http.get<EventDTO[]>(`${this.apiUrl}/chapter/${chapterId}`);
    }

    // -------------------- Participants (event committee / guests of honor / speakers) --------------------
    // Replaces the old embedded eventCommittee/eventGuestsOfHonor/eventSpeakers arrays on EventDTO.
    getEventParticipants(eventId: string, type?: EventParticipantType): Observable<EventParticipantDTO[]> {
        const url = `${this.apiUrl}/${eventId}/participants` + (type ? `?type=${type}` : '');
        return this.http.get<EventParticipantDTO[]>(url);
    }

    // Public/anonymous read — userId is stripped server-side before it reaches this response.
    getPublicEventParticipants(eventId: string, type?: EventParticipantType): Observable<EventParticipantDTO[]> {
        const url = `${this.apiUrl}/public/${eventId}/participants` + (type ? `?type=${type}` : '');
        return this.http.get<EventParticipantDTO[]>(url);
    }

    addEventParticipant(eventId: string, participant: EventParticipantDTO): Observable<EventParticipantDTO> {
        return this.http.post<EventParticipantDTO>(`${this.apiUrl}/${eventId}/participants`, participant);
    }

    updateEventParticipant(eventId: string, participantId: string, participant: EventParticipantDTO): Observable<EventParticipantDTO> {
        return this.http.put<EventParticipantDTO>(`${this.apiUrl}/${eventId}/participants/${participantId}`, participant);
    }

    deleteEventParticipant(eventId: string, participantId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${eventId}/participants/${participantId}`);
    }

    // -------------------- Event Registrations --------------------
    updateEventRegistration(id: string, registration: EventRegistrationDTO): Observable<EventDTO> {
        return this.http.put<EventDTO>(`${this.apiUrl}/register/${id}`, registration);
    }

    // -------------------- Expenses --------------------
    addExpense(eventId: string, expense: EventExpenseDTO): Observable<EventExpenseDTO> {
        return this.http.post<EventExpenseDTO>(`${this.apiUrl}/${eventId}/expenses`, expense);
    }

    updateExpense(eventId: string, expenseId: string, expense: EventExpenseDTO): Observable<EventExpenseDTO> {
        return this.http.put<EventExpenseDTO>(`${this.apiUrl}/${eventId}/expenses/${expenseId}`, expense);
    }

    deleteExpense(eventId: string, expenseId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${eventId}/expenses/${expenseId}`);
    }

    getExpenses(eventId: string): Observable<EventExpenseDTO[]> {
        return this.http.get<EventExpenseDTO[]>(`${this.apiUrl}/${eventId}/expenses`);
    }

    uploadExpenseReceipt(eventId: string, file: File): Observable<ExpenseReceiptUploadResponse> {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return this.http.put<ExpenseReceiptUploadResponse>(
            `${this.apiUrl}/${eventId}/expenses/upload-receipt`,
            formData
        );
    }

    downloadExpenseReceipt(eventId: string, filename: string): Observable<Blob> {
        return this.http.get(
            `${this.apiUrl}/${eventId}/expenses/download-receipt/${encodeURIComponent(filename)}`,
            {responseType: 'blob'}
        );
    }

    // -------------------- Close Event --------------------
    closeEvent(eventId: string, type?: string): Observable<void> {
        return this.http.post<void>(`${this.apiUrl}/${eventId}/close`, {});
    }

    // -------------------- Photos --------------------
    uploadPhoto(eventId: string, photo: EventPhotoDTO): Observable<EventPhotoDTO> {
        return this.http.post<EventPhotoDTO>(`${this.apiUrl}/${eventId}/photos`, photo);
    }

    getPhotos(eventId: string): Observable<EventPhotoDTO[]> {
        return this.http.get<EventPhotoDTO[]>(`${this.apiUrl}/${eventId}/photos`);
    }

    moderatePhoto(eventId: string, photoId: string, status: string): Observable<EventPhotoDTO> {
        return this.http.put<EventPhotoDTO>(`${this.apiUrl}/${eventId}/photos/${photoId}/moderate?status=${status}`, {});
    }

    deletePhoto(eventId: string, photoId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${eventId}/photos/${photoId}`);
    }

    // -------------------- Comments --------------------
    addComment(photoId: string, comment: EventCommentDTO): Observable<EventCommentDTO> {
        return this.http.post<EventCommentDTO>(`${this.apiUrl}/photos/${photoId}/comments`, comment);
    }

    getComments(photoId: string): Observable<EventCommentDTO[]> {
        return this.http.get<EventCommentDTO[]>(`${this.apiUrl}/photos/${photoId}/comments`);
    }

    // -------------------- Email --------------------
    sendEmailMessage(eventId: string, messageId: string, message: EventEmailMessageDTO): Observable<EventEmailMessageDTO> {
        return this.http.put<EventEmailMessageDTO>(`${this.apiUrl}/${eventId}/sendemails/${messageId}`, message);
    }

    // -------------------- Test Email --------------------
    sendTestEmailMessage(
        eventId: string,
        messageId: string,
        testEmail: string
    ): Observable<EventEmailMessageDTO> {
        return this.http.put<EventEmailMessageDTO>(
            `${this.apiUrl}/${eventId}/testemail/${messageId}`,
            { email: testEmail }
        );
    }

    // -------------------- Notification --------------------
    sendNotificationMessage(eventId: string): Observable<void> {
        return this.http.put<void>(
            `${this.apiUrl}/${eventId}/addnotifications`,
            null // ✅ REQUIRED
        );
    }


    addEmailMessage(eventId: string, message: EventEmailMessageDTO): Observable<EventEmailMessageDTO> {
        return this.http.post<EventEmailMessageDTO>(`${this.apiUrl}/${eventId}/emails`, message);
    }

    updateEmailMessage(eventId: string, messageId: string, message: EventEmailMessageDTO): Observable<EventEmailMessageDTO> {
        return this.http.put<EventEmailMessageDTO>(`${this.apiUrl}/${eventId}/emails/${messageId}`, message);
    }

    deleteEmailMessage(eventId: string, messageId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${eventId}/emails/${messageId}`);
    }

    getAllEmailMessages(eventId: string): Observable<EventEmailMessageDTO[]> {
        return this.http.get<EventEmailMessageDTO[]>(`${this.apiUrl}/${eventId}/emails`);
    }

    // -------------------- Anonymous Event Registration --------------------
    getPublicEventDetails(id: string): Observable<EventDTO> {
        return this.http.get<EventDTO>(`${this.apiUrl}/public/${id}`);
    }

    // Anonymous nav menu feed: NEC-scope events currently OPEN for registration.
    getPublicOpenNecEvents(): Observable<PublicEventSummaryDTO[]> {
        return this.http.get<PublicEventSummaryDTO[]>(`${this.apiUrl}/public/nec-open`);
    }

    getPublicEventFull(id: string): Observable<EventDTO> {
        return this.http.get<EventDTO>(`${this.apiUrl}/public/${id}/full`);
    }

    verifyEmailForEvent(eventId: string, email: string): Observable<AnonymousEventVerifyResponse> {
        return this.http.post<AnonymousEventVerifyResponse>(`${this.apiUrl}/public/${eventId}/verify-email`, {email});
    }

    anonymousEventRegistration(eventId: string, registration: EventRegistrationDTO): Observable<EventDTO> {
        return this.http.put<EventDTO>(`${this.apiUrl}/public/${eventId}/register`, registration);
    }

    // -------------------- CONFERENCE Event Bookings --------------------
    /** Stripe card payments recorded for this event (organizers only). Amounts are in cents. */
    getCardPayments(eventId: string): Observable<CardPaymentDTO[]> {
        return this.http.get<CardPaymentDTO[]>(`${this.apiUrl}/${eventId}/card-payments`);
    }

    /** Re-reads one card payment from Stripe and returns its updated state. */
    syncCardPayment(eventId: string, paymentId: string): Observable<CardPaymentDTO> {
        return this.http.post<CardPaymentDTO>(`${this.apiUrl}/${eventId}/card-payments/${paymentId}/sync`, {});
    }

    getEventBookings(eventId: string): Observable<EventBookingDTO[]> {
        return this.http.get<EventBookingDTO[]>(`${this.apiUrl}/${eventId}/bookings`);
    }

    getEventBooking(eventId: string, bookingId: string): Observable<EventBookingDTO> {
        return this.http.get<EventBookingDTO>(`${this.apiUrl}/${eventId}/bookings/${bookingId}`);
    }

    createEventBooking(eventId: string, dto: EventBookingDTO): Observable<EventBookingDTO> {
        return this.http.post<EventBookingDTO>(`${this.apiUrl}/${eventId}/bookings`, dto);
    }

    updateEventBooking(eventId: string, bookingId: string, dto: EventBookingDTO): Observable<EventBookingDTO> {
        return this.http.put<EventBookingDTO>(`${this.apiUrl}/${eventId}/bookings/${bookingId}`, dto);
    }

    updateEventBookingStatus(eventId: string, bookingId: string, status: string): Observable<EventBookingDTO> {
        return this.http.put<EventBookingDTO>(
            `${this.apiUrl}/${eventId}/bookings/${bookingId}/status?status=${status}`, {}
        );
    }

    deleteEventBooking(eventId: string, bookingId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${eventId}/bookings/${bookingId}`);
    }

    verifyEmailForEventBooking(eventId: string, email: string): Observable<AnonymousBookingVerifyResponse> {
        return this.http.post<AnonymousBookingVerifyResponse>(
            `${this.apiUrl}/public/${eventId}/booking/verify`, { email }
        );
    }

    createPublicEventBooking(eventId: string, dto: EventBookingDTO): Observable<EventBookingDTO> {
        return this.http.post<EventBookingDTO>(`${this.apiUrl}/public/${eventId}/booking`, dto);
    }

    getPublicEventBookings(eventId: string): Observable<EventBookingDTO[]> {
        return this.http.get<EventBookingDTO[]>(`${this.apiUrl}/public/${eventId}/bookings`);
    }

    // -------------------- Edit / re-confirm an existing registration (CONFERENCE and LOCAL) --------------------
    /** Registrant (own, while the event is open and not started) or organizer (with a reason). Never changes status. */
    editRegistration(eventId: string, bookingId: string, request: RegistrationEditRequest): Observable<EventBookingDTO> {
        return this.http.put<EventBookingDTO>(`${this.apiUrl}/${eventId}/registrations/${bookingId}/edit`, request);
    }

    /** Organizers: records money received by hand and confirms; may waive a remainder (chapter executive / NEC). */
    confirmRegistration(eventId: string, bookingId: string, request: RegistrationConfirmRequest): Observable<EventBookingDTO> {
        return this.http.post<EventBookingDTO>(`${this.apiUrl}/${eventId}/registrations/${bookingId}/confirm`, request);
    }

    /** Organizers: moves a confirmed registration back to Payment Pending (reason required); payments already recorded are kept. */
    reopenRegistration(eventId: string, bookingId: string, request: RegistrationReopenRequest): Observable<EventBookingDTO> {
        return this.http.post<EventBookingDTO>(`${this.apiUrl}/${eventId}/registrations/${bookingId}/reopen`, request);
    }

    /** Organizers: marks an unpaid registration "didn't attend, no payment" (reason required). */
    markRegistrationNoShow(eventId: string, bookingId: string, reason: string): Observable<EventBookingDTO> {
        return this.http.post<EventBookingDTO>(`${this.apiUrl}/${eventId}/registrations/${bookingId}/no-show`, {reason});
    }

    /** Organizers: undoes the no-show mark, putting the registration back to Payment Pending (reason required). */
    restoreRegistrationNoShow(eventId: string, bookingId: string, reason: string): Observable<EventBookingDTO> {
        return this.http.post<EventBookingDTO>(`${this.apiUrl}/${eventId}/registrations/${bookingId}/no-show/undo`, {reason});
    }

    getRegistrationHistory(eventId: string, bookingId: string): Observable<RegistrationChangeDTO[]> {
        return this.http.get<RegistrationChangeDTO[]>(`${this.apiUrl}/${eventId}/registrations/${bookingId}/history`);
    }

    quickSignupForEvent(eventId: string, request: QuickSignupRequest): Observable<QuickSignupResponse> {
        return this.http.post<QuickSignupResponse>(`${this.apiUrl}/public/${eventId}/quick-signup`, request);
    }
}
